/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { useUI } from "../theme/ThemeProvider";
import { useNavigate } from "react-router-dom";
import {
  FiSave, FiUpload, FiLoader, FiMail, FiShare2, FiShield, FiEdit3, FiX,
  FiInstagram, FiFacebook, FiMessageCircle, FiTruck, FiLayout, FiSmartphone
} from "react-icons/fi";
import { FaSnapchatGhost, FaTiktok } from "react-icons/fa";
import WhatsLayout from "../layouts/WhatsLayout";
import { getTenantLogoUrl } from "../../utils/getTenantLogoUrl";

const THEMES_DATA = {
  1: [
    { name: "Classic Brown", primary: "#C18064" },
    { name: "Ocean Blue", primary: "#4A90E2" },
    { name: "Royal Purple", primary: "#9B59B6" },
    { name: "Coral Red", primary: "#E74C3C" },
  ],
  2: [
    { name: "Pink Glow", primary: "#EC4899" },
    { name: "Purple Dream", primary: "#A855F7" },
    { name: "Golden Luxe", primary: "#F59E0B" },
    { name: "Turquoise Glow", primary: "#14B8A6" },
  ], 3: [
    { name: "original", primary: "#5B4DFF" },
    { name: "royalBlue", primary: "#2563eb" },
    { name: "forestGreen", primary: "#059669" },
    { name: "burntOrange", primary: "#ea580c" },
  ],

};

export default function ProfileSettings() {
  const { theme, lang } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [tempFormData, setTempFormData] = useState({}); // For modal editing
  const [showModal, setShowModal] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const navigate = useNavigate();

  const tenantId = localStorage.getItem("tenantId") || 41;

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await axios.get(`https://madar-api.madar.iq/api/GetAllTenantFullDetails?tenantId=${tenantId}`);
        if (response.data && response.data.length > 0) {
          const data = response.data[0];
          setFormData(data);
          // معالجة رابط اللوجو
          if (data.Logo) {
            setLogoPreview(getTenantLogoUrl(data.Logo));
            localStorage.setItem("companyLogo", data.Logo);
          }
        }
      } catch (error) { console.error("Fetch Error:", error); }
      finally { setLoading(false); }
    };
    fetchDetails();
  }, [tenantId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault(); // Handle optional event
    setSaving(true);
    const dataToSend = new FormData();

    // خريطة مطابقة الأسماء من GET إلى POST (تحويل الحرف الأول لكبير ليناسب الـ API)
    const mapping = {
      tenantId: "TenantId", tenantName: "TenantName", email: "Email", phone: "Phone",
      facebook: "Facebook", instagram: "Instagram", snapchat: "Snapchat", tiktok: "Tiktok",
      whatsapp: "Whatsapp", privacyPolicy: "PrivacyPolicy", deliveryPolicy: "DeliveryPolicy",
      refundPolicy: "RefundPolicy", storeSlug: "StoreSlug", colorName: "ColorName",
      themeId: "ThemeId", sectorType: "SectorType"
    };

    Object.keys(formData).forEach((key) => {
      const apiKey = mapping[key] || key.charAt(0).toUpperCase() + key.slice(1);
      if (formData[key] !== null && key !== 'Logo') {
        dataToSend.append(apiKey, formData[key]);
      }
    });

    if (selectedFile) dataToSend.append("LogoFile", selectedFile);

    try {
      await axios.post("https://madar-api.madar.iq/eTenantDetails/UpdateTenantFullDetails", dataToSend);
      // تحديث البيانات محلياً فوراً لتنعكس في النافبار
      localStorage.setItem("tenantName", formData.TenantName);
      if (selectedFile) {
        localStorage.removeItem("companyLogo");
      } else if (formData.Logo) {
        localStorage.setItem("companyLogo", formData.Logo);
      }

      Swal.fire({
        icon: "success",
        title: isArabic ? "✅ تم الحفظ بنجاح" : "✅ Saved successfully",
        timer: 2000,
        showConfirmButton: false,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "خطأ" : "Error",
        text: error.message,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } finally { setSaving(false); }
  };

  // Open Modal
  const openModal = () => {
    setTempFormData({ ...formData });
    setShowModal(true);
  };

  // Close Modal
  const closeModal = () => {
    setShowModal(false);
  };

  // Save Modal Changes (Updates Local State Only + Checks Slug)
  const saveModalChanges = async () => {
    setModalSaving(true);

    // Validate Slug Uniqueness in Modal
    try {
      const validationRes = await axios.get(`https://madar-api.madar.iq/api/GetAllTenantFullDetails?tenantId=-1`);
      if (validationRes.data) {
        const allTenants = validationRes.data;
        const existing = allTenants.find(t =>
          parseInt(t.TenantId) !== parseInt(tenantId) &&
          ((t.storeSlug && t.storeSlug.toLowerCase() === tempFormData.storeSlug?.trim().toLowerCase()) ||
            (t.StoreSlug && t.StoreSlug.toLowerCase() === tempFormData.storeSlug?.trim().toLowerCase()))
        );
        if (existing) {
          setModalSaving(false);
          Swal.fire({
            icon: "error",
            title: isArabic ? "اسم مستخدم" : "Name Used",
            text: isArabic ? "اسم الرابط (Slug) مستخدم مسبقاً، يرجى اختيار اسم آخر" : "This store slug is already used by another store, please choose another one",
            background: isDark ? "#1E293B" : "#fff",
            color: isDark ? "#fff" : "#000",
          });
          return;
        }
      }
    } catch (err) {
      console.error("Failed to validate slug", err);
    }

    setModalSaving(false);
    setFormData({ ...tempFormData });
    closeModal();
  };

  // Handle Modal Input Change
  const handleModalChange = (e) => {
    const { name, value } = e.target;
    setTempFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><FiLoader className="animate-spin text-purple-600" size={40} /></div>;

  return (
    <WhatsLayout title={isArabic ? "إعدادات الصفحة الشخصية" : "Profile Settings"}>
      <div className="w-full max-w-5xl mx-auto pb-20 px-4">
        <form onSubmit={handleSubmit} className="space-y-6">

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* اليسار: الهوية والسوشيال */}
            <div className="lg:col-span-4 space-y-6">
              <div className={`p-6 rounded-3xl border ${isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-100 shadow-sm"}`}>
                <div className="relative w-28 h-28 mx-auto mb-4 group cursor-pointer">
                  <img src={logoPreview || "https://via.placeholder.com/150"} className="w-full h-full rounded-2xl object-cover border-2 border-purple-500/20" alt="Logo" />
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
                    <FiUpload className="text-white" />
                    <input type="file" hidden onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) { setSelectedFile(file); setLogoPreview(URL.createObjectURL(file)); }
                    }} />
                  </label>
                </div>
                <h2 className="text-center font-bold">{formData.TenantName}</h2>
              </div>

              <div className={`p-6 rounded-3xl border ${isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-100 shadow-sm"}`}>
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><FiShare2 className="text-purple-500" /> {isArabic ? "مواقع التواصل" : "Social Media"}</h3>
                <div className="space-y-3">
                  <SocialInput icon={<FiMessageCircle className="text-green-500" />} name="whatsapp" placeholder="WhatsApp" value={formData.whatsapp || ""} onChange={handleChange} isDark={isDark} />
                  <SocialInput icon={<FiFacebook className="text-blue-600" />} name="facebook" placeholder="Facebook" value={formData.facebook || ""} onChange={handleChange} isDark={isDark} />
                  <SocialInput icon={<FiInstagram className="text-pink-500" />} name="instagram" placeholder="Instagram" value={formData.instagram || ""} onChange={handleChange} isDark={isDark} />
                  <SocialInput icon={<FaSnapchatGhost className="text-yellow-500" />} name="snapchat" placeholder="Snapchat" value={formData.snapchat || ""} onChange={handleChange} isDark={isDark} />
                  <SocialInput icon={<FaTiktok className="text-black dark:text-white" />} name="tiktok" placeholder="Tiktok" value={formData.tiktok || ""} onChange={handleChange} isDark={isDark} />
                </div>
              </div>
            </div>

            {/* اليمين: البيانات الأساسية */}
            <div className="lg:col-span-8 space-y-6">
              <div className={`p-6 rounded-3xl border ${isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-100 shadow-sm"}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-bold flex items-center gap-2"><FiMail className="text-purple-500" /> {isArabic ? "المعلومات الأساسية" : "Basic Info"}</h3>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => navigate('/reset-password')} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 text-blue-600 hover:bg-blue-500 hover:text-white rounded-lg transition-colors text-xs font-bold">
                      <FiShield /> {isArabic ? "تغيير كلمة السر" : "Password"}
                    </button>
                    <button type="button" onClick={openModal} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-600 hover:bg-purple-500 hover:text-white rounded-lg transition-colors text-xs font-bold">
                      <FiEdit3 /> {isArabic ? "تعديل" : "Edit"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem label="Store Name" value={formData.TenantName} isDark={isDark} />
                  <InfoItem label="Store Slug (URL)" value={formData.storeSlug} isDark={isDark} />
                  <InfoItem label="Email" value={formData.Email} isDark={isDark} />
                  <InfoItem label="Phone" value={formData.Phone} isDark={isDark} />
                </div>
              </div>

              {/* الثيمات */}
              <div className={`p-6 rounded-3xl border ${isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-100 shadow-sm"}`}>
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><FiLayout className="text-purple-500" /> {isArabic ? "نمط المتجر" : "Store Theme"}</h3>
                {Object.keys(THEMES_DATA).map(tId => (
                  <div key={tId} className="mb-4 last:mb-0">
                    <p className="text-[10px] uppercase opacity-40 font-bold mb-2">{tId === "1" ? "Classic Collection" : "Neon Glow Collection"}</p>
                    <div className="flex flex-wrap gap-2">
                      {THEMES_DATA[tId].map(color => {
                        const active = formData.ThemeId === parseInt(tId) && formData.ColorName === color.name;
                        return (
                          <button key={color.name} type="button" onClick={() => setFormData({ ...formData, ThemeId: parseInt(tId), ColorName: color.name })}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border-2 transition-all ${active
                              ? "border-purple-500 bg-purple-50 dark:bg-slate-800 dark:text-white"
                              : "border-transparent bg-gray-100 dark:bg-slate-700 dark:text-gray-300"
                              }`}>
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: color.primary }} />
                            <span className="text-xs font-bold">{color.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* السياسات */}
              <div className={`p-6 rounded-3xl border ${isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-100 shadow-sm"}`}>
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><FiShield className="text-purple-500" /> {isArabic ? "سياسات المتجر" : "Store Policies"}</h3>
                <div className="space-y-4">
                  <PolicyArea label="Privacy Policy" name="privacyPolicy" value={formData.privacyPolicy || ""} onChange={handleChange} isDark={isDark} />
                  <PolicyArea label="Delivery Policy" name="deliveryPolicy" value={formData.deliveryPolicy || ""} onChange={handleChange} isDark={isDark} />
                  <PolicyArea label="Refund Policy" name="refundPolicy" value={formData.refundPolicy || ""} onChange={handleChange} isDark={isDark} />
                </div>
              </div>
            </div>
          </div>

          {/* زر الحفظ العائم أو المرتب في الأسفل */}
          <div className="flex justify-end sticky bottom-4 z-10">
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 bg-[#7400BF] hover:bg-[#5e009b] text-white px-8 py-3 rounded-2xl font-bold shadow-lg shadow-purple-500/30 transition-all active:scale-95 disabled:opacity-50">
              {saving ? <FiLoader className="animate-spin" /> : <FiSave />}
              {isArabic ? "حفظ التغييرات" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
      {/* Basic Info Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`w-full max-w-lg rounded-3xl p-6 shadow-2xl transform transition-all ${isDark ? "bg-slate-800 border border-slate-700 text-white" : "bg-white text-gray-800"}`}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold">{isArabic ? "تعديل المعلومات الأساسية" : "Edit Basic Info"}</h3>
              <button onClick={closeModal} className="p-2 rounded-full transition-colors">
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <Input label="Store Name" name="TenantName" value={tempFormData.TenantName || ""} onChange={handleModalChange} isDark={isDark} />
              <Input label="Store Slug (URL)" name="storeSlug" value={tempFormData.storeSlug || ""} onChange={handleModalChange} isDark={isDark} />
              <Input label="Email" name="Email" value={tempFormData.Email || ""} onChange={handleModalChange} isDark={isDark} disabled />
              <Input label="Phone" name="Phone" value={tempFormData.Phone || ""} onChange={handleModalChange} isDark={isDark} />
            </div>

            <div className="flex gap-3 justify-end mt-8">
              <button
                type="button"
                onClick={closeModal}
                className={`px-5 py-2.5 rounded-xl font-bold transition-all ${isDark ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
              >
                {isArabic ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="button"
                disabled={modalSaving}
                onClick={saveModalChanges}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold bg-[#7400BF] hover:bg-[#5e009b] text-white shadow-lg shadow-purple-500/30 transition-all active:scale-95 disabled:opacity-50"
              >
                {modalSaving && <FiLoader className="animate-spin" />}
                {isArabic ? "حفظ" : "Save"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </WhatsLayout>
  );
}

function InfoItem({ label, value, isDark }) {
  return (
    <div className={`p-4 rounded-2xl border ${isDark ? "bg-slate-700/50 border-slate-600 text-gray-300" : "bg-gray-50 border-gray-100 text-gray-700"}`}>
      <span className="block text-[10px] font-bold uppercase opacity-50 mb-1">{label}</span>
      <p className={`font-semibold text-sm truncate ${isDark ? "text-white" : "text-gray-900"}`}>{value || "-"}</p>
    </div>
  );
}

// Components
function Input({ label, isDark, ...props }) {
  return (
    <div>
      <label className="text-[10px] font-bold opacity-40 mb-1 block px-1 uppercase">{label}</label>
      <input {...props} className={`w-full p-2.5 rounded-xl border text-sm outline-none focus:border-purple-500 ${isDark ? "!bg-slate-700 !border-slate-600 !text-white" : "bg-gray-50 border-gray-200 text-gray-800"} ${props.disabled ? "opacity-60 cursor-not-allowed" : ""}`} />
    </div>
  );
}

function PolicyArea({ label, isDark, ...props }) {
  return (
    <div>
      <label className="text-[10px] font-bold opacity-40 mb-1 block px-1 uppercase">{label}</label>
      <textarea {...props} rows="2" className={`w-full p-3 rounded-xl border text-sm outline-none focus:border-purple-500 ${isDark ? "!bg-slate-700 !border-slate-600 !text-white" : "bg-gray-50 border-gray-200 text-gray-800"}`} />
    </div>
  );
}

function SocialInput({ icon, isDark, ...props }) {
  return (
    <div className={`flex items-center gap-2 p-1.5 rounded-xl border ${isDark ? "bg-slate-700 border-slate-600" : "bg-gray-50 border-gray-200"}`}>
      <div className={`w-8 h-8 flex items-center justify-center rounded-lg shadow-sm ${isDark ? "bg-slate-600" : "bg-white"}`}>{icon}</div>
      <input {...props} className={`bg-transparent flex-1 outline-none text-xs font-medium ${isDark ? "!text-white" : "text-gray-800"}`} />
    </div>
  );
}
